/**
 * Resolve whether the Flow Designer's warm-target sandbox environment is
 * Sandboxes v2-backed, and if so, its default-branch readiness.
 *
 * The workflow-detail response's `environments` array (see
 * `FlowDesigner.tsx`'s `workflowEnvironments`) carries only `sandboxTemplateId`
 * (v1), not the `sandboxV2Environment` marker — so this independently reads the
 * property's own environment list (the same route
 * `PropertyEnvironmentsTab` uses) to find them, then reads readiness from
 * `GET /org/sandboxes-v2/environments/:environmentId/readiness`
 * (`useSandboxV2EnvironmentReadiness`).
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../../../../config';
import { toApiError } from '../../../../lib/api-error';
import { useSandboxV2EnvironmentReadiness } from '../../../../hooks/useSandboxV2EnvironmentReadiness';

type PropertyEnvironmentV2Fields = {
  id: string;
  sandboxV2Environment: string | null;
};

export type SandboxV2DesignerTarget = {
  isV2: boolean;
  isLoading: boolean;
  environment: string | null;
  /** `'ready' | 'warming' | 'stale' | 'none'`, or null while unresolved/loading. */
  status: 'ready' | 'warming' | 'stale' | 'none' | null;
  /** The snapshot's version (commit SHA or push id). Set for `status === 'ready'` or `'stale'`. */
  version: string | null;
  /** The last build's failure, when `status === 'none'` and one was attempted. */
  error: string | null;
};

const NOT_V2: SandboxV2DesignerTarget = {
  isV2: false,
  isLoading: false,
  environment: null,
  status: null,
  version: null,
  error: null,
};

export function useSandboxV2DesignerTarget(
  propertyId: string | null,
  environmentId: string | null,
  enabled: boolean
): SandboxV2DesignerTarget {
  const active = enabled && !!propertyId && !!environmentId;

  const envListQ = useQuery({
    queryKey: ['property-environments', propertyId],
    enabled: active,
    queryFn: async (): Promise<{ items: PropertyEnvironmentV2Fields[] }> => {
      const r = await fetch(`${API_URL}/org/properties/${propertyId}/environments`, {
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, 'Failed to fetch environments');
      return r.json();
    },
  });

  const item = envListQ.data?.items.find((i) => i.id === environmentId) ?? null;
  const isV2 = !!item?.sandboxV2Environment;
  const environment = isV2 ? item!.sandboxV2Environment : null;

  const readinessQ = useSandboxV2EnvironmentReadiness(environmentId, active && isV2);

  return useMemo<SandboxV2DesignerTarget>(() => {
    if (!active) return NOT_V2;
    if (!isV2) {
      if (envListQ.isLoading) return { ...NOT_V2, isLoading: true };
      return NOT_V2;
    }
    const error = readinessQ.isError
      ? readinessQ.error instanceof Error
        ? readinessQ.error.message
        : 'Failed to read this environment\'s readiness.'
      : envListQ.isError
        ? 'Failed to resolve this environment.'
        : (readinessQ.data?.error ?? null);
    return {
      isV2: true,
      isLoading: envListQ.isLoading || readinessQ.isLoading,
      environment,
      status: readinessQ.data?.status ?? null,
      version: readinessQ.data?.version ?? null,
      error,
    };
  }, [
    active,
    isV2,
    envListQ.isLoading,
    envListQ.isError,
    readinessQ.isLoading,
    readinessQ.isError,
    readinessQ.error,
    readinessQ.data,
    environment,
  ]);
}
